import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface SupervisorTaskStats {
  completed: number
  completedOnTime: number
  inProgress: number
  notStarted: number
  overdue: number
  total: number
}

export interface TaskDetailsReportState {
  totalTaskAssignment: number
  totalCompletedTasks: number
  totalInProgressTasks: number
  totalNotStartedTasks: number
  totalAssignedTeamMember: number
  listOfTasks: Array<{
    _id: string       // taskId as string (e.g. "1"), used as join key
    taskId: string    // same as _id
    id?: number       // optional numeric id (not returned by API but kept for compatibility)
    title?: string    // optional title (not returned by API but kept for compatibility)
    assignedDate: string
    dueDate?: string
    assignedTeamMembers: number
    unassignedTeamMembers: number
    inProgressTeamMembers: number
    completedTeamMembers: number
    completedOnTimeTeamMembers: number
    overdueTeamMembers: number
    notStartedTeamMembers: number
    supervisors: string[]
    operationalManagers: string[]
    functionalAreas: string[]
    // Per-supervisor breakdown: supervisorName -> stats (provided by backend for filtered metric calculations)
    supervisorStats?: Record<string, SupervisorTaskStats>
  }>
  loading: boolean
  error: string | null
}

const initialState: TaskDetailsReportState = {
  totalTaskAssignment: 0,
  totalCompletedTasks: 0,
  totalInProgressTasks: 0,
  totalNotStartedTasks: 0,
  totalAssignedTeamMember: 0,
  listOfTasks: [],
  loading: false,
  error: null,
}

export const taskDetailsReportSlice = createSlice({
  name: 'taskDetailsReport',
  initialState,
  reducers: {
    setTaskDetailsReportLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setTaskDetailsReportError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setTaskDetailsReport: (
      state,
      action: PayloadAction<Omit<TaskDetailsReportState, 'loading' | 'error'>>,
    ) => {
      state.totalTaskAssignment = action.payload.totalTaskAssignment
      state.totalCompletedTasks = action.payload.totalCompletedTasks
      state.totalInProgressTasks = action.payload.totalInProgressTasks
      state.totalNotStartedTasks = action.payload.totalNotStartedTasks
      state.totalAssignedTeamMember = action.payload.totalAssignedTeamMember
      state.listOfTasks = action.payload.listOfTasks
      state.loading = false
      state.error = null
    },
    clearTaskDetailsReport: (state) => {
      state.totalTaskAssignment = 0
      state.totalCompletedTasks = 0
      state.totalInProgressTasks = 0
      state.totalNotStartedTasks = 0
      state.totalAssignedTeamMember = 0
      state.listOfTasks = []
      state.loading = false
      state.error = null
    },
  },
})

export const {
  setTaskDetailsReportLoading,
  setTaskDetailsReportError,
  setTaskDetailsReport,
  clearTaskDetailsReport,
} = taskDetailsReportSlice.actions

export default taskDetailsReportSlice.reducer
